/**
 * Emoji suggestions utility for task icons
 * Provides categorized emojis and keyword-based suggestions
 */

// All available emojis organized by category
export const EMOJI_CATEGORIES = {
  favorites: ['⭐', '🪥', '👕', '📚', '🛁', '🍽️', '🛏️', '🎒', '🎵', '⏰'],
  hygiene: ['🪥', '🛁', '🧴', '💇', '🧼', '🪒', '💅', '🧽', '🚿', '🧻'],
  clothing: ['👕', '👗', '👖', '👟', '🧥', '👘', '🩱', '🧦', '👔', '🎽'],
  food: ['🍽️', '🥣', '🍳', '🥗', '🍎', '🥪', '🍕', '🥞', '🍪', '🥤', '☕', '🥛', '🥘', '🍝', '🥗'],
  school: ['📚', '📝', '🎒', '✏️', '📓', '🔢', '🔬', '🎨', '✍️', '📖', '🧮', '📐'],
  activities: ['🏃', '⚽', '🎮', '🎵', '🎨', '🏊', '🚴', '🎾', '🎯', '🎪', '🎭', '🎳'],
  music: ['🎵', '🎹', '🎸', '🎺', '🎷', '🥁', '🎤', '🎼', '🎻', '🪕'],
  sleep: ['🛏️', '💤', '🌙', '🌛', '😴', '🧸', '🛌', '⭐'],
  home: ['🏠', '🧹', '🧺', '🪣', '🛋️', '🪴', '🛠️', '📺', '🖥️', '💡'],
  outdoors: ['🌳', '🚗', '🚌', '🚶', '🌞', '☀️', '🌧️', '⛅', '🏖️', '🏕️'],
  people: ['👨‍👩‍👧', '👨‍👩‍👦', '👶', '👧', '👦', '👨', '👩', '🧒', '👴', '👵'],
  time: ['⏰', '⏱️', '🕐', '🕑', '🕒', '🕓', '🕔', '🕕', '🕖', '🕗'],
  wellness: ['🧘', '💪', '🏋️', '🧘‍♀️', '🧘‍♂️', '🏃‍♀️', '🚶‍♀️', '🤸', '🧗'],
  rewards: ['🏆', '🥇', '🎖️', '⭐', '🌟', '💎', '🎁', '🎉', '👏', '✅'],
  transport: ['🚗', '🚌', '🚕', '🚙', '🚎', '🚐', '🛴', '🚲', '🚶', '✈️'],
  nature: ['🌸', '🌺', '🌻', '🌷', '🌱', '🌿', '🍀', '🌴', '🌲', '🦋'],
  animals: ['🐶', '🐱', '🐰', '🐻', '🦊', '🐼', '🐨', '🦁', '🐸', '🐢'],
  emotions: ['😊', '😄', '🥰', '😴', '🤔', '😎', '🥳', '😌', '😃', '🙂'],
};

// Flat list of all emojis for general use
export const ALL_EMOJIS = Object.values(EMOJI_CATEGORIES).flat();

// Remove duplicates from ALL_EMOJIS
export const UNIQUE_EMOJIS = Array.from(new Set(ALL_EMOJIS));

// Keyword to emoji mapping for suggestions
const KEYWORD_MAPPINGS: Record<string, string[]> = {
  // Hygiene
  brush: ['🪥', '💇', '🧹'],
  teeth: ['🪥', '😁'],
  tooth: ['🪥'],
  bath: ['🛁', '🚿', '🧴'],
  shower: ['🚿', '🛁', '🧴'],
  wash: ['🧼', '🛁', '🚿', '🧴', '🧽'],
  clean: ['🧹', '🧼', '🧽', '🪣'],
  hair: ['💇', '🧴'],
  
  // Clothing
  dress: ['👗', '👕', '👔'],
  clothes: ['👕', '👗', '👖'],
  shirt: ['👕', '🎽'],
  pants: ['👖'],
  shoes: ['👟', '👞'],
  pajama: ['🛌', '🛏️', '👘'],
  pj: ['🛌', '👘'],
  put: ['👕', '👖'],
  get: ['👕', '👗', '🎒'],
  ready: ['👕', '🎒', '✅'],
  
  // Food
  eat: ['🍽️', '🥣', '🍴'],
  breakfast: ['🥞', '🍳', '🥣', '☕'],
  lunch: ['🥪', '🍽️', '🥗'],
  dinner: ['🍽️', '🥘', '🍝'],
  snack: ['🍎', '🍪', '🥤'],
  food: ['🍽️', '🥘', '🍕'],
  cook: ['👨‍🍳', '🍳', '🥘'],
  meal: ['🍽️', '🥣', '🥗'],
  cereal: ['🥣', '🥛'],
  milk: ['🥛', '🥣'],
  drink: ['🥤', '☕', '🥛'],
  water: ['💧', '🥤'],
  
  // School & Learning
  school: ['🏫', '🎒', '📚'],
  homework: ['📝', '✏️', '📓'],
  study: ['📚', '📖', '📝'],
  read: ['📖', '📚', '📕'],
  book: ['📖', '📚', '📕'],
  write: ['✍️', '📝', '✏️'],
  learn: ['📚', '🧮', '🔬'],
  math: ['🔢', '🧮', '📐'],
  science: ['🔬', '🧪', '🔭'],
  art: ['🎨', '🖌️', '✏️'],
  draw: ['✏️', '🎨', '🖌️'],
  
  // Activities & Sports
  play: ['🎮', '⚽', '🎨', '🎵'],
  game: ['🎮', '🎲', '🎯'],
  sport: ['⚽', '🏀', '🎾'],
  soccer: ['⚽'],
  football: ['🏈', '⚽'],
  basketball: ['🏀'],
  swim: ['🏊', '🏊‍♀️', '🩱'],
  bike: ['🚴', '🚲'],
  run: ['🏃', '🏃‍♀️', '👟'],
  walk: ['🚶', '🚶‍♀️'],
  exercise: ['💪', '🏋️', '🏃'],
  yoga: ['🧘', '🧘‍♀️'],
  dance: ['💃', '🕺', '🎵'],
  
  // Music
  music: ['🎵', '🎶', '🎤'],
  piano: ['🎹'],
  guitar: ['🎸'],
  sing: ['🎤', '🎵'],
  practice: ['🎵', '📚', '🎹'],
  instrument: ['🎹', '🎸', '🎺'],
  violin: ['🎻'],
  drum: ['🥁'],
  
  // Sleep & Bedtime
  sleep: ['💤', '😴', '🛏️'],
  bed: ['🛏️', '🛌', '💤'],
  nap: ['💤', '😴', '🛏️'],
  wake: ['⏰', '☀️', '🌅'],
  morning: ['☀️', '🌅', '⏰'],
  night: ['🌙', '🌛', '⭐'],
  bedtime: ['🛏️', '🌙', '💤'],
  rest: ['😴', '🛋️', '💤'],
  
  // Home & Chores
  room: ['🛏️', '🧹', '🏠'],
  chore: ['🧹', '🧺', '🪣'],
  tidy: ['🧹', '🏠'],
  organize: ['📦', '🗂️'],
  trash: ['🗑️', '🚮'],
  laundry: ['🧺', '👕'],
  vacuum: ['🧹'],
  dishes: ['🍽️', '🧽'],
  
  // Transport & Going places
  drive: ['🚗', '🚙'],
  car: ['🚗', '🚙'],
  bus: ['🚌', '🚎'],
  leave: ['🚗', '🚶', '🎒'],
  go: ['🚗', '🚶', '🏠'],
  travel: ['✈️', '🚗', '🧳'],
  pick: ['🚗', '👨‍👩‍👧'],
  drop: ['🚗', '🏫'],
  
  // Time-related
  time: ['⏰', '🕐', '⏱️'],
  clock: ['⏰', '🕐'],
  alarm: ['⏰', '🔔'],
  wait: ['⏳', '⏰'],
  
  // Rewards & Achievements
  reward: ['🏆', '🎁', '⭐'],
  star: ['⭐', '🌟', '✨'],
  win: ['🏆', '🥇'],
  done: ['✅', '👏', '🎉'],
  finish: ['✅', '🏁', '👏'],
  complete: ['✅', '👏'],
  
  // Generic
  task: ['📝', '✅', '📋'],
  activity: ['🎯', '📋', '✅'],
  routine: ['📋', '📅', '⏰'],
  warm: ['🔥', '☀️', '🌡️'],
  up: ['⬆️', '☀️', '⏰'],
  scale: ['🎹', '🎵', '📏'],
};

/**
 * Get suggested emojis based on task name
 * @param taskName - The name of the task to get suggestions for
 * @param maxSuggestions - Maximum number of suggestions to return (default 8)
 * @returns Array of suggested emojis
 */
export function getSuggestedEmojis(taskName: string, maxSuggestions: number = 8): string[] {
  if (!taskName || taskName.trim().length === 0) {
    return EMOJI_CATEGORIES.favorites.slice(0, maxSuggestions);
  }
  
  const lowerName = taskName.toLowerCase();
  const words = lowerName.split(/\s+/);
  const suggestions = new Set<string>();
  
  // Check each word against keyword mappings
  for (const word of words) {
    // Direct match
    if (KEYWORD_MAPPINGS[word]) {
      KEYWORD_MAPPINGS[word].forEach(emoji => suggestions.add(emoji));
    }
    
    // Partial match (word starts with keyword or keyword starts with word)
    for (const [keyword, emojis] of Object.entries(KEYWORD_MAPPINGS)) {
      if (keyword.startsWith(word) || word.startsWith(keyword)) {
        emojis.slice(0, 3).forEach(emoji => suggestions.add(emoji));
      }
    }
  }
  
  // If we have suggestions, return them
  if (suggestions.size > 0) {
    const result = Array.from(suggestions).slice(0, maxSuggestions);
    
    // Pad with favorites if needed
    if (result.length < maxSuggestions) {
      for (const emoji of EMOJI_CATEGORIES.favorites) {
        if (!result.includes(emoji)) {
          result.push(emoji);
          if (result.length >= maxSuggestions) break;
        }
      }
    }
    
    return result;
  }
  
  // Default to favorites
  return EMOJI_CATEGORIES.favorites.slice(0, maxSuggestions);
}

/**
 * Get emojis by category
 * @param category - Category name
 * @returns Array of emojis in that category
 */
export function getEmojisByCategory(category: keyof typeof EMOJI_CATEGORIES): string[] {
  return EMOJI_CATEGORIES[category] || [];
}

/**
 * Get all category names
 * @returns Array of category names
 */
export function getEmojiCategories(): Array<keyof typeof EMOJI_CATEGORIES> {
  return Object.keys(EMOJI_CATEGORIES) as Array<keyof typeof EMOJI_CATEGORIES>;
}
